import type {
  ConsentStatus,
  LegalDocumentPayload,
  MeResponse,
  MatchRequestOption,
  MatchResponse,
  ProgressResponse,
  ProgressSummary,
  SaveSlotList,
  SaveSlotMeta,
  SaveSlotRecord,
} from './types.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: unknown,
  ) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });

  const text = await response.text();
  const body: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const code =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `http_${response.status}`;
    throw new ApiError(response.status, code, body);
  }
  return body as T;
}

export const api = {
  me: () => request<MeResponse>('/api/auth/me'),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  legalDocuments: () =>
    request<{ documents: LegalDocumentPayload[] }>('/api/legal/documents').then((r) => r.documents),

  acceptConsent: (versions: { eula: string; privacy: string }) =>
    request<{ consent: ConsentStatus }>('/api/consent', {
      method: 'POST',
      body: JSON.stringify({ accept: versions, agreed: true }),
    }).then((r) => r.consent),

  /**
   * Ask the server which authored option an utterance meant. The LLM key lives there; this is the
   * only reason the endpoint exists rather than the browser calling a model directly.
   */
  matchUtterance: (utterance: string, options: MatchRequestOption[]) =>
    request<MatchResponse>('/api/matcher/match', {
      method: 'POST',
      body: JSON.stringify({ utterance, options }),
    }),

  getProgress: () => request<ProgressResponse>('/api/progress'),

  putProgress: (
    data: Record<string, unknown>,
    baseRevision: number | null,
    summary?: ProgressSummary,
  ) =>
    request<ProgressResponse>('/api/progress', {
      method: 'PUT',
      body: JSON.stringify({ data, baseRevision, ...(summary ? { summary } : {}) }),
    }),

  /** The four manual save slots (metadata only). */
  listSaves: () => request<SaveSlotList>('/api/saves'),

  /** The full blob for one slot, for loading it into the active game. */
  loadSlot: (slot: number) => request<SaveSlotRecord>(`/api/saves/${slot}`),

  /** Write the current game state into a slot, overwriting whatever was there. */
  saveToSlot: (slot: number, data: Record<string, unknown>, summary?: ProgressSummary) =>
    request<SaveSlotMeta>(`/api/saves/${slot}`, {
      method: 'PUT',
      body: JSON.stringify({ data, ...(summary ? { summary } : {}) }),
    }),

  /** Clear a slot. */
  deleteSlot: (slot: number) => request<void>(`/api/saves/${slot}`, { method: 'DELETE' }),
};

/** The URL that starts the Google OAuth redirect dance. */
export function signInUrl(returnTo = '/play'): string {
  return `/api/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
}
